import { EU_COUNTRIES } from './constants';

/**
 * Thuế eBay thu hộ trên line item: `ebayCollectAndRemitTaxes[].ebayReference` {name, value}.
 * name là loại thuế, value có thể kèm tiền tố: EU {IOSS, IM2760000742}, AU {GST, ABN#64652016681}.
 * Trước 2026-10 màn ship chỉ lấy name === 'IOSS'; nay lấy MỌI loại và hiển thị đúng tên.
 * BE (shippo-api/ebay-tax-id.ts) map sang tax_id.type Shippo + ghi notes "<tên> <số>".
 */
export type TEbayTax = { name: string; value: string };

// DDG = eBay Delivery Duty Guarantee, không phải mã thuế.
const IGNORED_TAX_NAMES = ['DDG'];

type TTaxLineItem = {
  ebayCollectAndRemitTaxes?: Array<{ ebayReference?: { name?: string; value?: string } } | undefined>;
};

export const ebayTaxKey = (tax: TEbayTax) => `${tax.name} ${tax.value}`;

const dedupe = (taxes: TEbayTax[]) =>
  taxes.filter((tax, index) => taxes.findIndex((other) => ebayTaxKey(other) === ebayTaxKey(tax)) === index);

export const extractEbayTaxes = (lineItem?: TTaxLineItem): TEbayTax[] =>
  dedupe(
    (lineItem?.ebayCollectAndRemitTaxes ?? [])
      .map((tax) => ({
        name: tax?.ebayReference?.name?.trim().toUpperCase() || '',
        value: tax?.ebayReference?.value?.trim() || '',
      }))
      .filter((tax) => tax.name && tax.value && !IGNORED_TAX_NAMES.includes(tax.name)),
  );

export const mergeEbayTaxes = (lists: TEbayTax[][]): TEbayTax[] => dedupe(([] as TEbayTax[]).concat(...lists));

/**
 * Ship không gắn đơn eBay (khách tự gõ số): không có tên từ eBay -> đoán theo nước đến, dùng đúng tên eBay đặt
 * (AU: name "GST", value "ABN#...") để BE map cùng bảng: IOSS→IOSS, GST→ARN, còn lại→VAT.
 */
export const guessTaxNameByCountry = (country?: string) => {
  if (country && EU_COUNTRIES.includes(country)) return 'IOSS';
  if (country === 'AU' || country === 'NZ') return 'GST';
  return 'VAT';
};

/** Nhãn ô thuế: tên loại đang chọn; chưa chọn mà có nhiều loại thì ghép "IOSS/VOEC". */
export const ebayTaxLabel = (selectedName: string | undefined, options: TEbayTax[], fallback = 'IOSS') =>
  selectedName || Array.from(new Set(options.map((tax) => tax.name))).join('/') || fallback;
